import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Syncronize component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconSyncronize extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M11.2497 1.85522C11.2497 1.44101 11.5855 1.10522 11.9997 1.10522C14.1546 1.10522 16.261 1.74421 18.0527 2.94136C19.8444 4.13852 21.2408 5.84009 22.0654 7.83089C22.89 9.82169 23.1058 12.0123 22.6854 14.1257C22.265 16.2392 21.2274 18.1805 19.7037 19.7042C18.18 21.2278 16.2387 22.2655 14.1252 22.6859C12.0118 23.1063 9.82121 22.8905 7.83041 22.0659C5.8396 21.2413 4.13804 19.8448 2.94088 18.0532C1.7438 16.2616 1.10483 14.1553 1.10474 12.0006C1.10224 9.66279 1.78016 7.39219 3.03191 5.45538L2.03657 5.70421C1.63473 5.80468 1.22752 5.56035 1.12706 5.15851C1.0266 4.75666 1.27092 4.34946 1.67277 4.249L4.79431 3.46862C4.94674 3.43051 5.09995 3.44201 5.23689 3.49291C5.29761 3.51537 5.35605 3.54598 5.41055 3.58477C5.49974 3.64807 5.57544 3.73097 5.63045 3.82914C5.66571 3.8919 5.69127 3.95872 5.70708 4.02739L6.4842 7.13586C6.58466 7.5377 6.34034 7.94491 5.93849 8.04537C5.53665 8.14583 5.12945 7.90151 5.02898 7.49966L4.60649 5.80969C3.31002 7.60022 2.60227 9.76472 2.60474 11.9994L2.60474 12.0002C2.60474 13.8584 3.15575 15.6748 4.18809 17.2198C5.22042 18.7648 6.68772 19.969 8.40443 20.6801C10.1211 21.3912 12.0102 21.5772 13.8326 21.2147C15.6551 20.8522 17.3291 19.9574 18.643 18.6435C19.9569 17.3296 20.8517 15.6555 21.2142 13.8331C21.5767 12.0106 21.3907 10.1216 20.6796 8.40491C19.9685 6.6882 18.7643 5.2209 17.2193 4.18857C15.6743 3.15623 13.8579 2.60522 11.9997 2.60522C11.5855 2.60522 11.2497 2.26944 11.2497 1.85522ZM1.10474 12.0006L1.10474 12.0011L1.85431 12.0002H1.10474V12.0006ZM12.7498 6.53735C12.7498 6.12314 12.414 5.78735 11.9998 5.78735C11.5855 5.78735 11.2498 6.12314 11.2498 6.53735V12.7804C11.2498 13.0645 11.4103 13.3242 11.6643 13.4512L15.7223 15.4802C16.0928 15.6655 16.5433 15.5153 16.7286 15.1448C16.9138 14.7744 16.7636 14.3238 16.3932 14.1386L12.7498 12.3169V6.53735Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-syncronize') === undefined) {
  customElements.define('cat-icon-syncronize', BlocksIconSyncronize);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-syncronize': BlocksIconSyncronize;
  }
}
